/**
 * Default chart palette — exported and themeable.
 *
 * Replaces the private DEFAULT_COLORS that used to live inside ChartRenderer.
 * Inject a custom palette via RenderContext.palette; consumers can read
 * DEFAULT_PALETTE / colorAt directly to stay consistent with the charts.
 */

export const DEFAULT_PALETTE: readonly string[] = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#F97316',
];

/**
 * Semantic role colors — for non-series chrome (reference/limit/identity/trend
 * lines). Centralized so these stop being hardcoded across layout.ts and
 * scatter/index.ts. Series colors still come from DEFAULT_PALETTE / colorAt;
 * these are for meaning, not category cycling.
 */
export const SEMANTIC = {
  trend: '#111827',
  identity: '#9CA3AF',
  reference: '#6B7280',
  limitUpper: '#EF4444',
  limitLower: '#10B981',
  limitBand: '#F97316',
} as const;

/**
 * Pick the color at `index`, cycling modulo the palette length so any number
 * of series/categories gets a stable color. Falls back to DEFAULT_PALETTE when
 * no palette is given.
 */
export function colorAt(index: number, palette: readonly string[] = DEFAULT_PALETTE): string {
  if (palette.length === 0) return DEFAULT_PALETTE[0];
  const i = ((index % palette.length) + palette.length) % palette.length;
  return palette[i];
}

/**
 * Darken a `#RRGGBB` color by `factor` (0–1) for readable text on white — e.g.
 * data labels keep their series association but gain contrast (a light orange
 * like #F59E0B is hard to read at full chroma). Non-hex input is returned as-is.
 */
export function darken(hex: string, factor = 0.65): string {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = Math.round(((n >> 16) & 0xff) * factor);
  const g = Math.round(((n >> 8) & 0xff) * factor);
  const b = Math.round((n & 0xff) * factor);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}
